/**
 * VoIP Context - Global state management for telephony features
 * 
 * This context manages the SIP connection, call state, and provides
 * actions for making/receiving calls.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import useToast from "../hooks/useToast";
// 🚀 Fase 2.1: Lazy load do sipService.
// SIP.js (~150KB gz) só entra no bundle quando o usuário tenta conectar.
// `peekSipService()` retorna null se ainda não carregado (no-op safe em cleanups).
// `loadSipService()` faz dynamic import e cacheia a instância singleton.
import { loadSipService, peekSipService, TransferState } from "../services/sipServiceLoader";
import { setTakeoverHandler } from "../services/sipTabLock";
import api from "../services/api";
import { i18n } from "../translate/i18n";
import contactCache from "../services/contactCache";
import pushNotificationService from "../services/pushNotificationService";

// Call states
export const CallState = {
  IDLE: "idle",
  RINGING_IN: "ringing_in",
  RINGING_OUT: "ringing_out",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  ON_HOLD: "on_hold",
  ENDED: "ended",
};

// Re-export TransferState for components
export { TransferState };

// LocalStorage keys
const STORAGE_KEYS = {
  AUTO_CONNECT: "softphone-auto-connect",
  NOTIFICATIONS: "softphone-notifications",
  CALL_VOLUME: "softphone-call-volume",
  RING_VOLUME: "softphone-ring-volume",
  MISSED_CALLS: "softphone-missed-calls", // 🚀 Fase 0.3
};

// 🚀 Fase 2.1: Atalho síncrono para o sipService (lazy).
// Retorna null se o módulo ainda não foi carregado — todos os usos são
// no formato `_sip()?.method()` para serem no-op safe pré-connect.
const _sip = () => peekSipService();

// Helper to get from localStorage
const getStorageValue = (key, defaultValue) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved === null) return defaultValue;
    if (key.includes("volume")) return parseFloat(saved);
    return JSON.parse(saved);
  } catch (e) {
    console.error(`[VoIP] getStorageValue error: ${key}`, e);
    return defaultValue;
  }
};

// Helper to save to localStorage
const setStorageValue = (key, value) => {
  try {
    const valueToSave = typeof value === "boolean" ? JSON.stringify(value) : String(value);
    localStorage.setItem(key, valueToSave);
  } catch (e) {
    console.error("[VoIP] Failed to save to localStorage:", e);
  }
};

// 🔧 FIX: Rejeitar invitations enfileiradas com 486 Busy Here.
// Sem isso, o chamador remoto continua ouvindo o ringback indefinidamente
// quando a chamada principal termina ou é rejeitada.
const rejectAllQueuedInvitations = (callQueue) => {
  if (!Array.isArray(callQueue) || callQueue.length === 0) return;
  for (const queued of callQueue) {
    try {
      const invitation = queued?.invitation;
      if (invitation && typeof invitation.reject === "function") {
        const result = invitation.reject({ statusCode: 486 });
        if (result && typeof result.catch === "function") {
          result.catch(() => {});
        }
      }
    } catch (e) {
      console.warn("[VoIP] Failed to reject queued invitation:", e?.message);
    }
  }
};

// Initial state
const initialState = {
  isEnabled: false,
  isRegistered: false,
  isConnecting: false,
  isReconnecting: false, // 🔧 Estado de reconexão
  reconnectAttempt: 0,
  callState: CallState.IDLE,
  callInfo: null,
  callStartTime: null,
  isMuted: false,
  isOnHold: false,
  // 🔄 Transfer state - Estado da Arte
  isInTransfer: false,
  transferState: TransferState.IDLE,
  transferInfo: {
    originalNumber: null,
    originalName: null,
    consultNumber: null,
    consultName: null,
    startTime: null,
  },
  transferTarget: "", // Legacy - mantido para compatibilidade
  attendedParty: "",
  incomingCall: null,
  callQueue: [], // Fila de chamadas entrantes
  extension: null,
  callDuration: 0,
  currentCallId: null,
  // 📱 Softphone drawer state
  softphoneOpen: false,
  // 📞 Número para discar (preenchido externamente)
  dialNumber: "",
  // 🔧 Qualidade de conexão (heartbeat)
  connectionQuality: {
    status: "unknown", // "good", "poor", "unknown"
    latency: -1,
  },
  // Settings com localStorage
  autoConnect: getStorageValue(STORAGE_KEYS.AUTO_CONNECT, false),
  notifications: getStorageValue(STORAGE_KEYS.NOTIFICATIONS, true),
  callVolume: getStorageValue(STORAGE_KEYS.CALL_VOLUME, 0.8),
  ringVolume: getStorageValue(STORAGE_KEYS.RING_VOLUME, 0.6),
  // 🚀 Fase 0.3: Histórico de chamadas perdidas
  missedCallsCount: getStorageValue(STORAGE_KEYS.MISSED_CALLS, 0),
  // 🚀 Fase 1.5: Circuit breaker (após 10 falhas em 5min)
  circuitBreakerOpen: false,
};

export const VoipContext = createContext();

// 🚀 PERF (Fase 0.1): Contexts separados para reduzir re-renders.
// - VoipStateContext: muda a cada segundo (callDuration). Use só onde precisar de state.
// - VoipActionsContext: callbacks estáveis (memoizadas). Use em componentes "passivos".
// O legacy `useVoip()` continua funcionando, mas penaliza performance — migre quando puder.
export const VoipStateContext = createContext();
export const VoipActionsContext = createContext();

export const useVoip = () => {
  const context = useContext(VoipContext);
  if (!context) {
    throw new Error("useVoip must be used within a VoipProvider");
  }
  return context;
};

export const useVoipState = () => {
  const context = useContext(VoipStateContext);
  if (!context) {
    throw new Error("useVoipState must be used within a VoipProvider");
  }
  return context;
};

export const useVoipActions = () => {
  const context = useContext(VoipActionsContext);
  if (!context) {
    throw new Error("useVoipActions must be used within a VoipProvider");
  }
  return context;
};

export const VoipProvider = ({ children }) => {
  const toast = useToast();
  const [state, setState] = useState(initialState);
  const callTimerRef = useRef(null);
  const ringtoneAudioRef = useRef(null);
  const hangupAudioRef = useRef(null);
  const hangupTimeoutRef = useRef(null);
  const callAudioRef = useRef(null);
  const extensionRef = useRef(state.extension);
  const endedSafetyTimerRef = useRef(null);
  const processingIncomingRef = useRef(false);
  const reconnectToastIdRef = useRef(null);
  const handleCallStateChangeRef = useRef(null);
  const handleIncomingCallRef = useRef(null);
  const handleRegistrationChangeRef = useRef(null);

  useEffect(() => { extensionRef.current = state.extension; }, [state.extension]);

  // Initialize audio elements
  useEffect(() => {
    if (typeof window !== "undefined") {
      // Ringtone audio (toca quando alguém liga para o ramal)
      ringtoneAudioRef.current = new Audio("/sounds/ringtone.ogg");
      ringtoneAudioRef.current.loop = true;
      ringtoneAudioRef.current.volume = state.ringVolume;
      
      // Hangup audio (toca quando a chamada não conecta)
      hangupAudioRef.current = new Audio("/sounds/hangup.ogg");
      hangupAudioRef.current.volume = state.ringVolume;
      
      // Call audio (managed by sipService, but we can control volume)
      callAudioRef.current = document.getElementById("sip-call-audio");
      if (callAudioRef.current) {
        // HTML audio element aceita apenas 0-1, boost é via Web Audio API
        callAudioRef.current.volume = Math.min(state.callVolume, 1);
      }
      
      // Aplicar volume com boost via sipService (no-op se ainda não carregado)
      _sip()?.setCallVolume(state.callVolume);
    }

    const handleStorageChange = (e) => {
      if (e.key?.startsWith('softphone-')) {
        setState((prev) => ({
          ...prev,
          callVolume: getStorageValue(STORAGE_KEYS.CALL_VOLUME, prev.callVolume),
          ringVolume: getStorageValue(STORAGE_KEYS.RING_VOLUME, prev.ringVolume),
          autoConnect: getStorageValue(STORAGE_KEYS.AUTO_CONNECT, prev.autoConnect),
          notifications: getStorageValue(STORAGE_KEYS.NOTIFICATIONS, prev.notifications),
        }));
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      stopCallTimer();
      const sip = _sip();
      if (sip) {
        sip.onCallStateChange = null;
        sip.onIncomingCall = null;
        sip.onRegistrationChange = null;
        sip.onConnectionQuality = null;
        sip.onTransferStateChange = null;
        sip.onReconnecting = null;
        sip.onReconnectFailed = null;
        sip.onCircuitBreakerOpen = null;
        sip.dispose();
      }
      if (ringtoneAudioRef.current) {
        ringtoneAudioRef.current.pause();
        ringtoneAudioRef.current.removeAttribute('src');
        ringtoneAudioRef.current.load();
        ringtoneAudioRef.current = null;
      }
      if (hangupAudioRef.current) {
        hangupAudioRef.current.pause();
        hangupAudioRef.current.removeAttribute('src');
        hangupAudioRef.current.load();
        hangupAudioRef.current = null;
      }
      if (hangupTimeoutRef.current) {
        clearTimeout(hangupTimeoutRef.current);
      }
      if (endedSafetyTimerRef.current) {
        clearTimeout(endedSafetyTimerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Takeover handler — another tab wants to connect, we must release
  useEffect(() => {
    setTakeoverHandler(() => {
      const sip = _sip();
      if (sip?.isInCall()) return false;

      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }
      if (endedSafetyTimerRef.current) {
        clearTimeout(endedSafetyTimerRef.current);
        endedSafetyTimerRef.current = null;
      }
      if (hangupTimeoutRef.current) {
        clearTimeout(hangupTimeoutRef.current);
        hangupTimeoutRef.current = null;
      }
      if (ringtoneAudioRef.current) ringtoneAudioRef.current.pause();

      if (sip) {
        sip.onCallStateChange = null;
        sip.onIncomingCall = null;
        sip.onRegistrationChange = null;
        sip.onConnectionQuality = null;
        sip.onTransferStateChange = null;
        sip.onReconnecting = null;
        sip.onReconnectFailed = null;
        sip.onCircuitBreakerOpen = null;
        sip.dispose();
      }

      if (reconnectToastIdRef.current != null) {
        toast.removeToast(reconnectToastIdRef.current);
        reconnectToastIdRef.current = null;
      }

      setState({
        ...initialState,
        autoConnect: getStorageValue(STORAGE_KEYS.AUTO_CONNECT, false),
        notifications: getStorageValue(STORAGE_KEYS.NOTIFICATIONS, true),
        callVolume: getStorageValue(STORAGE_KEYS.CALL_VOLUME, 0.8),
        ringVolume: getStorageValue(STORAGE_KEYS.RING_VOLUME, 0.6),
      });

      setTimeout(() => toast.info(i18n.t("voip.movedToOtherTab") || "Softphone movido para outra aba."), 0);
      return true;
    });

    return () => setTakeoverHandler(null);
  }, [toast]);

  // Start call duration timer
  const startCallTimer = useCallback(() => {
    if (callTimerRef.current) return;

    callTimerRef.current = setInterval(() => {
      setState((prev) => {
        if (!prev.callStartTime) return prev;
        return {
          ...prev,
          callDuration: Math.floor((Date.now() - prev.callStartTime) / 1000),
        };
      });
    }, 1000);
  }, []);

  // Stop call duration timer
  const stopCallTimer = useCallback(() => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
  }, []);

  // Play ringtone
  const playRingtone = useCallback(() => {
    if (ringtoneAudioRef.current) {
      ringtoneAudioRef.current.volume = state.ringVolume;
      ringtoneAudioRef.current.currentTime = 0;
      ringtoneAudioRef.current.play().catch(() => {});
    }
  }, [state.ringVolume]);

  // Stop ringtone
  const stopRingtone = useCallback(() => {
    if (ringtoneAudioRef.current) {
      ringtoneAudioRef.current.pause();
      ringtoneAudioRef.current.currentTime = 0;
    }
  }, []);

  // Play hangup sound
  const playHangup = useCallback(() => {
    if (hangupAudioRef.current) {
      hangupAudioRef.current.volume = state.ringVolume;
      hangupAudioRef.current.currentTime = 0;
      hangupAudioRef.current.play().catch(() => {});
    }
  }, [state.ringVolume]);

  // Handle call state changes from SIP service
  const handleCallStateChange = useCallback(
    (sipState, data = {}) => {
      switch (sipState) {
        case "ringing":
          setState((prev) => ({
            ...prev,
            callState: CallState.RINGING_OUT,
          }));
          break;

        case "establishing":
          setState((prev) => ({
            ...prev,
            callState: CallState.CONNECTING,
          }));
          break;

        case "connected":
        case "answered":
          stopRingtone();
          const startTime = Date.now();
          setState((prev) => ({
            ...prev,
            callState: CallState.CONNECTED,
            callStartTime: startTime,
            callDuration: 0,
            incomingCall: null,
            callInfo: {
              ...prev.callInfo,
              startTime: startTime,
            },
          }));
          startCallTimer();
          break;

        case "terminating":
          setState((prev) => ({
            ...prev,
            callState: CallState.ENDED,
          }));
          if (endedSafetyTimerRef.current) clearTimeout(endedSafetyTimerRef.current);
          endedSafetyTimerRef.current = setTimeout(() => {
            setState((prev) => {
              if (prev.callState === CallState.ENDED) {
                // 🔧 FIX: Rejeitar invitations enfileiradas (486 Busy) para que o
                // chamador remoto saiba que a chamada não foi atendida e o ringback pare.
                rejectAllQueuedInvitations(prev.callQueue);
                return { ...prev, callState: CallState.IDLE, callInfo: null, currentCallId: null, callQueue: [] };
              }
              return prev;
            });
          }, 5000);
          break;

        case "ended":
        case "rejected":
          stopRingtone();
          stopCallTimer();
          playHangup();
          if (endedSafetyTimerRef.current) {
            clearTimeout(endedSafetyTimerRef.current);
            endedSafetyTimerRef.current = null;
          }
          
          // Capturar dados para API ANTES do setState, evitando side-effects no callback
          let pendingCallUpdate = null;
          let queuedToReject = [];
          let newMissedCount = null;
          setState((prev) => {
            if (prev.currentCallId) {
              pendingCallUpdate = {
                callId: prev.currentCallId,
                status: sipState === "rejected" ? "no_answer" : "completed",
                duration: prev.callDuration || 0,
              };
            }
            queuedToReject = prev.callQueue || [];

            // 🚀 Fase 0.3: Detectar chamada perdida - inbound que terminou sem ter sido conectada.
            // Estados RINGING_IN/CONNECTING (inbound antes de aceitar) → ENDED sem CONNECTED = perdida.
            const wasInbound = prev.callInfo?.direction === "inbound" || prev.incomingCall != null;
            const wasNeverConnected = !prev.callStartTime;
            const isMissed = wasInbound && wasNeverConnected &&
              [CallState.RINGING_IN, CallState.CONNECTING, CallState.ENDED].includes(prev.callState);
            const updatedMissed = isMissed ? (prev.missedCallsCount || 0) + 1 : prev.missedCallsCount;
            if (isMissed) newMissedCount = updatedMissed;

            return {
              ...prev,
              callState: CallState.IDLE,
              callInfo: null,
              callStartTime: null,
              callDuration: 0,
              isMuted: false,
              isOnHold: false,
              incomingCall: null,
              currentCallId: null,
              callQueue: [],
              isInTransfer: false,
              transferState: TransferState.IDLE,
              transferInfo: {
                originalNumber: null,
                originalName: null,
                consultNumber: null,
                consultName: null,
                startTime: null,
              },
              missedCallsCount: updatedMissed,
            };
          });

          if (newMissedCount != null) {
            setStorageValue(STORAGE_KEYS.MISSED_CALLS, newMissedCount);
          }

          if (pendingCallUpdate) {
            api.put(`/api/voip/calls/${pendingCallUpdate.callId}`, {
              status: pendingCallUpdate.status,
              endedAt: new Date().toISOString(),
              duration: pendingCallUpdate.duration,
            }).catch(err => console.error("[VoIP] Failed to update call:", err));
          }

          // 🔧 FIX: Rejeitar invitations enfileiradas (486 Busy) para que o
          // chamador remoto saiba que a chamada não foi atendida e o ringback pare.
          rejectAllQueuedInvitations(queuedToReject);
          break;
        default:
          // Estado desconhecido - ignorar
          break;
      }
    },
    [startCallTimer, stopCallTimer, stopRingtone, playHangup]
  );

  useEffect(() => { handleCallStateChangeRef.current = handleCallStateChange; }, [handleCallStateChange]);

  // Lookup contact by phone number
  // 🔧 Implementa cache-first strategy para reduzir chamadas à API
  const lookupContact = useCallback(async (phoneNumber) => {
    try {
      // Check cache first
      const cached = contactCache.get(phoneNumber);
      if (cached) {
        return cached;
      }
      
      // Fetch from API
      const { data } = await api.get(`/api/voip/lookup/${encodeURIComponent(phoneNumber)}`);
      
      // Store in cache
      contactCache.set(phoneNumber, data);
      
      return data;
    } catch (error) {
      console.error("[VoIP] Failed to lookup contact:", error);
      return { contact: null, ticket: null };
    }
  }, []);

  // Handle incoming calls
  // Ringtone toca IMEDIATAMENTE; lookup de contato e registro rodam em background
  const handleIncomingCall = useCallback(
    async (callData) => {
      if (processingIncomingRef.current) {
        try { callData.invitation?.reject?.(); } catch {}
        return;
      }
      processingIncomingRef.current = true;

      try {
        const callerNumber = callData.from;
        const callerName = callData.displayName || callerNumber;

        // 1. IMEDIATAMENTE: tocar ringtone + mostrar UI com dados básicos
        let shouldPlayRingtone = false;
        let shouldShowNotification = false;

        setState((prev) => {
          if (prev.callState !== CallState.IDLE) {
            return {
              ...prev,
              callQueue: [...prev.callQueue, { from: callerNumber, displayName: callerName, invitation: callData.invitation }],
            };
          }

          shouldPlayRingtone = true;
          shouldShowNotification = prev.notifications;

          return {
            ...prev,
            callState: CallState.RINGING_IN,
            incomingCall: {
              sessionId: callData.invitation?.id || Date.now().toString(),
              from: callerNumber,
              displayName: callerName,
              contact: null,
              ticket: null,
              avatarUrl: null,
              invitation: callData.invitation,
            },
            callInfo: {
              direction: "inbound",
              remoteNumber: callerNumber,
              remoteName: callerName,
            },
          };
        });

        if (shouldPlayRingtone) {
          playRingtone();

          if (shouldShowNotification && pushNotificationService.getPermission() === "granted") {
            pushNotificationService.showNotification(i18n.t("voip.incomingCall"), {
              body: callerName,
              icon: "/favicon.ico",
              tag: "voip-incoming-call",
              requireInteraction: true,
            });
          }
        } else {
          return;
        }

        // 2. EM BACKGROUND: enriquecer com dados de contato (paralelo)
      const uniqueId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const [lookupSettled, registerSettled] = await Promise.allSettled([
        lookupContact(callerNumber),
          api.post("/api/voip/calls", {
            uniqueId,
            fromExtension: callerNumber,
            toNumber: extensionRef.current || "unknown",
            direction: "inbound",
          }),
        ]);

        const lookupResult = lookupSettled.status === "fulfilled" ? lookupSettled.value : { contact: null, ticket: null };
        const callId = registerSettled.status === "fulfilled" ? registerSettled.value.data.id : null;
        const contactName = lookupResult.contact?.name || callerName;

        setState((prev) => {
          if (prev.callState !== CallState.RINGING_IN) return prev;
          return {
            ...prev,
            currentCallId: callId,
            incomingCall: {
              ...prev.incomingCall,
              uniqueId,
              displayName: contactName,
              contact: lookupResult.contact,
              ticket: lookupResult.ticket,
              avatarUrl: lookupResult.contact?.profilePicUrl,
            },
            callInfo: {
              ...prev.callInfo,
              remoteName: contactName,
              contactId: lookupResult.contact?.id,
              ticketId: lookupResult.ticket?.id,
            },
          };
        });

        // Atualizar notificação com nome do contato (se mudou)
        if (contactName !== callerName && pushNotificationService.getPermission() === "granted") {
          pushNotificationService.showNotification(i18n.t("voip.incomingCall"), {
            body: contactName,
            icon: lookupResult.contact?.profilePicUrl || "/favicon.ico",
            tag: "voip-incoming-call",
            requireInteraction: true,
          });
        }

        // Atualizar registro com contactId/ticketId
        if (callId && lookupResult.contact?.id) {
          api.put(`/api/voip/calls/${callId}`, {
            contactId: lookupResult.contact.id,
            ticketId: lookupResult.ticket?.id,
          }).catch(() => {});
        }
      } finally {
        processingIncomingRef.current = false;
      }
    },
    [playRingtone, lookupContact]
  );

  useEffect(() => { handleIncomingCallRef.current = handleIncomingCall; }, [handleIncomingCall]);

  // Handle registration state changes
  const handleRegistrationChange = useCallback((registered) => {
    if (registered && reconnectToastIdRef.current != null) {
      toast.removeToast(reconnectToastIdRef.current);
      reconnectToastIdRef.current = null;
    }
    setState((prev) => {
      if (!registered && prev.isRegistered) {
        setTimeout(() => toast.warning(i18n.t("voip.disconnected")), 0);
      }
      return {
        ...prev,
        isRegistered: registered,
        isConnecting: false,
        isReconnecting: registered ? false : prev.isReconnecting,
        reconnectAttempt: registered ? 0 : prev.reconnectAttempt,
      };
    });
  }, [toast]);

  useEffect(() => { handleRegistrationChangeRef.current = handleRegistrationChange; }, [handleRegistrationChange]);

  // ============ SETTINGS FUNCTIONS ============

  // Set call volume (supports amplification up to 200%)
  const setCallVolume = useCallback((volume) => {
    // Permitir volume de 0 a 2.0 (0% a 200%)
    const safeVolume = Math.max(0, Math.min(2, volume));
    setStorageValue(STORAGE_KEYS.CALL_VOLUME, safeVolume);
    
    // 🔊 Usar sipService para amplificação via Web Audio API
    _sip()?.setCallVolume(safeVolume);
    
    // Fallback: elemento audio (limitado a 100%)
    if (callAudioRef.current) {
      callAudioRef.current.volume = Math.min(safeVolume, 1);
    }
    
    setState((prev) => ({ ...prev, callVolume: safeVolume }));
  }, []);

  // Set ring volume
  const setRingVolume = useCallback((volume) => {
    const safeVolume = Math.max(0, Math.min(1, volume));
    setStorageValue(STORAGE_KEYS.RING_VOLUME, safeVolume);
    
    // Update ringtone audio element
    if (ringtoneAudioRef.current) {
      ringtoneAudioRef.current.volume = safeVolume;
    }
    
    setState((prev) => ({ ...prev, ringVolume: safeVolume }));
  }, []);

  // Set auto-connect
  const setAutoConnect = useCallback((enabled) => {
    setStorageValue(STORAGE_KEYS.AUTO_CONNECT, enabled);
    setState((prev) => ({ ...prev, autoConnect: enabled }));
  }, []);

  // Set notifications
  const setNotifications = useCallback((enabled) => {
    setStorageValue(STORAGE_KEYS.NOTIFICATIONS, enabled);
    setState((prev) => ({ ...prev, notifications: enabled }));
  }, []);

  // 🚀 Fase 0.3: Limpar contador de chamadas perdidas
  const clearMissedCalls = useCallback(() => {
    setStorageValue(STORAGE_KEYS.MISSED_CALLS, 0);
    setState((prev) => ({ ...prev, missedCallsCount: 0 }));
  }, []);

  // 🚀 Fase 1.5: Reset manual do circuit breaker (botão "Tentar novamente")
  const resetCircuitBreaker = useCallback(() => {
    setState((prev) => ({ ...prev, circuitBreakerOpen: false }));
    _sip()?.resetCircuitBreaker();
  }, []);

  // 📱 Softphone drawer control
  const openSoftphone = useCallback(() => {
    setState((prev) => ({ ...prev, softphoneOpen: true }));
  }, []);

  const closeSoftphone = useCallback(() => {
    setState((prev) => ({ ...prev, softphoneOpen: false }));
  }, []);

  const toggleSoftphone = useCallback(() => {
    setState((prev) => ({ ...prev, softphoneOpen: !prev.softphoneOpen }));
  }, []);

  // 📞 Set dial number (para preencher o campo de discagem externamente)
  const setDialNumber = useCallback((number) => {
    setState((prev) => ({ ...prev, dialNumber: number || "" }));
  }, []);

  // 📞 Clear dial number
  const clearDialNumber = useCallback(() => {
    setState((prev) => ({ ...prev, dialNumber: "" }));
  }, []);

  // Notify backend about connection status
  const notifyConnectionStatus = useCallback(async (status, extension, errorDetails = null) => {
    try {
      await api.post("/api/voip/connection", { 
        status, 
        extension,
        errorDetails: errorDetails ? {
          message: errorDetails.message || String(errorDetails),
          code: errorDetails.code || errorDetails.name || null,
        } : null
      });
    } catch (error) {
      console.error("[VoIP] Failed to notify connection status:", error);
    }
  }, []);

  // Connect to SIP server
  const connect = useCallback(async () => {
    try {
      // Set connecting state
      setState((prev) => ({ ...prev, isConnecting: true }));

      // Fetch VoIP configuration from backend
      const { data } = await api.get("/api/voip/config");

      if (!data.enabled) {
        toast.info(i18n.t("voip.notEnabled"));
        setState((prev) => ({ ...prev, isConnecting: false }));
        return false;
      }

      // 🚀 Fase 2.1: Carregar sipService sob demanda (dynamic import → chunk separado)
      const sipService = await loadSipService();

      // Aplicar volume persistido (não foi aplicado no mount porque o módulo era lazy)
      try {
        const savedVolume = getStorageValue(STORAGE_KEYS.CALL_VOLUME, 0.8);
        sipService.setCallVolume(savedVolume);
      } catch (e) {
        console.warn("[VoIP] Failed to apply saved call volume:", e?.message);
      }

      // Set up SIP service callbacks via refs to prevent stale closures
      sipService.onCallStateChange = (...args) => handleCallStateChangeRef.current?.(...args);
      sipService.onIncomingCall = (...args) => handleIncomingCallRef.current?.(...args);
      sipService.onRegistrationChange = (registered) => {
        handleRegistrationChangeRef.current?.(registered);
        if (registered) {
          notifyConnectionStatus("connected", data.extension);
        }
      };
      
      // 🔄 Callback para estado de transferência
      sipService.onTransferStateChange = (transferData) => {
        setState((prev) => {
          const newState = {
            ...prev,
            isInTransfer: transferData.isInTransfer,
            transferState: transferData.state,
            transferInfo: transferData.info,
            transferTarget: transferData.info?.consultNumber || "",
          };
          
          // Atualizar isOnHold quando transferência é INICIADA
          if (
            transferData.state === TransferState.INITIATING ||
            transferData.state === TransferState.CONSULTING ||
            transferData.state === TransferState.CONSULT_RINGING
          ) {
            newState.isOnHold = true;
            newState.callState = CallState.ON_HOLD;
          }

          if (transferData.state === TransferState.CONSULT_CONNECTED) {
            newState.callState = CallState.CONNECTED;
            newState.isOnHold = false;
          }
          
          // Restaurar callState para CONNECTED quando transferência é cancelada/falha/idle
          if (
            (transferData.state === TransferState.CANCELLED || 
             transferData.state === TransferState.FAILED ||
             transferData.state === TransferState.IDLE) &&
            !transferData.isInTransfer
          ) {
            if (sipService.currentSession || sipService.transferOriginalSession) {
              newState.callState = CallState.CONNECTED;
              newState.isOnHold = false;
            }
            
            // 🔧 FIX: Limpar transferInfo quando transferência termina (cancelada/falha/idle)
            // Isso permite que o modal de transferência mostre todos os ramais novamente
            newState.transferInfo = {
              originalNumber: null,
              originalName: null,
              consultNumber: null,
              consultName: null,
              startTime: null,
            };
            newState.transferTarget = "";
          }
          
          return newState;
        });
      };
      
      // 🔧 Callback para reconexão em andamento
      // 🚀 PERF (Fase 0.2): Reconexão silenciosa nas 2 primeiras tentativas.
      // Drops de WebSocket <2s são comuns e geralmente resolvem sozinhos.
      // Só notificamos o usuário a partir da 3ª tentativa para reduzir ruído.
      const SILENT_RECONNECT_ATTEMPTS = 2;
      sipService.onReconnecting = (attempt, maxAttempts) => {
        setState((prev) => ({
          ...prev,
          isReconnecting: true,
          reconnectAttempt: attempt,
        }));

        if (attempt > SILENT_RECONNECT_ATTEMPTS && reconnectToastIdRef.current == null) {
          try {
            reconnectToastIdRef.current = toast.warning(
              `Reconectando ao servidor VoIP... (${attempt}/${maxAttempts})`,
              { persistent: true }
            );
          } catch (e) {
            console.warn("[VoIP] Failed to show reconnect toast:", e?.message);
          }
        }
      };
      
      sipService.onReconnectFailed = () => {
        // Mantido como rede de segurança para casos onde onReconnecting não foi chamado
        // (ex: falha imediata sem retry).
        if (reconnectToastIdRef.current == null) {
          try {
            reconnectToastIdRef.current = toast.warning(
              "Falha ao reconectar ao servidor VoIP. Tentando novamente...",
              { persistent: true }
            );
          } catch (e) {
            console.warn("[VoIP] Failed to show reconnect-failed toast:", e?.message);
          }
        }
      };

      // 🚀 Fase 1.5: Circuit breaker aberto → notifica usuário
      sipService.onCircuitBreakerOpen = ({ failureCount, windowMs }) => {
        setState((prev) => ({ ...prev, circuitBreakerOpen: true }));
        if (reconnectToastIdRef.current != null) {
          try { toast.removeToast(reconnectToastIdRef.current); } catch {}
          reconnectToastIdRef.current = null;
        }
        try {
          toast.error(
            `Falha persistente de conexão (${failureCount} tentativas em ${Math.round(windowMs/60000)}min). Clique no telefone para tentar novamente.`,
            { persistent: true }
          );
        } catch (e) {
          console.warn("[VoIP] Failed to show circuit-breaker toast:", e?.message);
        }
      };
      
      // 🔧 Callback para qualidade de conexão (heartbeat)
      sipService.onConnectionQuality = (quality) => {
        setState((prev) => ({
          ...prev,
          connectionQuality: quality,
        }));
      };

      const connected = await sipService.connect({
        wssUrl: data.wssUrl,
        extension: data.extension,
        password: data.password,
        domain: data.domain,
        displayName: data.displayName,
        iceServers: data.iceServers,
        steal: true,
      });

      if (!connected) {
        setState((prev) => ({ ...prev, isConnecting: false }));
        return false;
      }

      setState((prev) => ({
        ...prev,
        isEnabled: true,
        isConnecting: false,
        extension: data.extension,
      }));

      return true;
    } catch (error) {
      const sip = _sip();
      if (sip) {
        sip.onCallStateChange = null;
        sip.onIncomingCall = null;
        sip.onRegistrationChange = null;
        sip.onConnectionQuality = null;
        sip.onTransferStateChange = null;
        sip.onReconnecting = null;
        sip.onReconnectFailed = null;
        sip.onCircuitBreakerOpen = null;
      }

      if (error.message === 'SIP_ACTIVE_CALL_OTHER_TAB') {
        console.warn("[VoIP] Active call in another tab — cannot take over");
        toast.warning(i18n.t("voip.activeCallOtherTab") || "Não é possível conectar: chamada ativa em outra aba.");
        setState((prev) => ({ ...prev, isConnecting: false }));
        return false;
      }
      if (error.message === 'SIP_ALREADY_REGISTERED_OTHER_TAB') {
        console.warn("[VoIP] SIP already registered in another tab");
        toast.warning(i18n.t("voip.alreadyRegisteredOtherTab") || "Softphone já conectado em outra aba.");
        setState((prev) => ({ ...prev, isConnecting: false }));
        return false;
      }
      if (error.message === 'Connection already in progress') {
        console.warn("[VoIP] Connection already in progress");
        setState((prev) => ({ ...prev, isConnecting: false }));
        return false;
      }
      console.error("[VoIP] Failed to connect:", error);
      toast.error(i18n.t("voip.connectionFailed"));
      setState((prev) => ({ ...prev, isConnecting: false }));
      notifyConnectionStatus("failed", null, error);
      return false;
    }
  }, [notifyConnectionStatus, toast]);

  // Auto-connect on mount if enabled (delay absorbs Web Lock release on refresh)
  useEffect(() => {
    if (!state.autoConnect || state.isRegistered || state.isConnecting) return;
    const timer = setTimeout(() => connect(), 800);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Disconnect from SIP server
  const disconnect = useCallback(async () => {
    try {
      stopCallTimer();
      stopRingtone();
      const extension = extensionRef.current;

      const sip = _sip();
      if (sip) {
        sip.onCallStateChange = null;
        sip.onIncomingCall = null;
        sip.onRegistrationChange = null;
        sip.onConnectionQuality = null;
        sip.onTransferStateChange = null;
        sip.onReconnecting = null;
        sip.onReconnectFailed = null;
        sip.onCircuitBreakerOpen = null;
      }

      if (reconnectToastIdRef.current != null) {
        toast.removeToast(reconnectToastIdRef.current);
        reconnectToastIdRef.current = null;
      }

      await sip?.disconnect();
      notifyConnectionStatus("disconnected", extension);
    } catch (error) {
      console.error("[VoIP] Failed to disconnect:", error);
    } finally {
      setState({
        ...initialState,
        autoConnect: getStorageValue(STORAGE_KEYS.AUTO_CONNECT, false),
        notifications: getStorageValue(STORAGE_KEYS.NOTIFICATIONS, true),
        callVolume: getStorageValue(STORAGE_KEYS.CALL_VOLUME, 0.8),
        ringVolume: getStorageValue(STORAGE_KEYS.RING_VOLUME, 0.6),
      });
    }
  }, [notifyConnectionStatus, stopCallTimer, stopRingtone, toast]);

  // Make an outgoing call
  const makeCall = useCallback(
    async (destination, contactInfo = null) => {
      if (!state.isRegistered) {
        toast.warning(i18n.t("voip.notRegistered"));
        return false;
      }

      const sip = _sip();
      if (!sip) {
        toast.warning(i18n.t("voip.notRegistered"));
        return false;
      }
      if (sip.isInCall()) {
        toast.warning(i18n.t("voip.alreadyInCall"));
        return false;
      }

      try {
        // Generate unique call ID
        const uniqueId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        setState((prev) => ({
          ...prev,
          callState: CallState.RINGING_OUT,
          callInfo: {
            direction: "outbound",
            remoteNumber: destination,
            remoteName: contactInfo?.name || destination,
            contactId: contactInfo?.id,
            ticketId: null,
            startTime: null,
          },
        }));

        await sip.makeCall(destination);

        // Fire-and-forget: registrar no backend sem bloquear o fluxo
        api.post("/api/voip/calls", {
          uniqueId,
          fromExtension: extensionRef.current || state.extension,
          toNumber: destination,
          direction: "outbound",
          contactId: contactInfo?.id,
        }).then(({ data }) => {
          // 🔧 FIX: Race condition em chamadas curtas. Se a chamada já terminou
          // (callState: IDLE) antes do POST resolver, NÃO setar currentCallId,
          // senão um futuro update operaria sobre uma chamada já encerrada e
          // a próxima chamada inbound poderia ter seu ID sobrescrito.
          setState((prev) => {
            if (prev.callState === CallState.IDLE) return prev;
            return { ...prev, currentCallId: data.id };
          });
        }).catch(err => {
          console.error("[VoIP] Failed to register call:", err);
        });

        return true;
      } catch (error) {
        console.error("[VoIP] Failed to make call:", error);
        setState((prev) => ({
          ...prev,
          callState: CallState.IDLE,
          callInfo: null,
          currentCallId: null,
        }));
        toast.error(i18n.t("voip.callFailed"));
        return false;
      }
    },
    [state.isRegistered, state.extension, toast]
  );

  // Answer incoming call
  const answerCall = useCallback(async () => {
    try {
      stopRingtone();
      await _sip()?.answerCall();
      
      // 🔧 FIX: Forçar atualização do estado para CONNECTED
      // Isso garante que a UI atualize mesmo se o callback não for disparado corretamente
      setState((prev) => {
        if (prev.callState === CallState.RINGING_IN) {
          const startTime = Date.now();
          return {
            ...prev,
            callState: CallState.CONNECTED,
            callStartTime: startTime,
            callDuration: 0,
            incomingCall: null,
            callInfo: {
              ...prev.callInfo,
              startTime: startTime,
            },
          };
        }
        return prev;
      });
      
      // Iniciar timer se ainda não iniciado
      startCallTimer();
      
      return true;
    } catch (error) {
      console.error("[VoIP] ❌ Failed to answer call:", error);
      if (error.message === 'ODHK_NO_VALID_DTLS_FINGERPRINT') {
        toast.error("Chamada rejeitada: conexão sem criptografia DTLS válida.");
        setState((prev) => ({
          ...prev,
          callState: CallState.IDLE,
          incomingCall: null,
          callInfo: null,
          currentCallId: null,
        }));
        return false;
      }
      toast.error(i18n.t("voip.answerFailed"));
      return false;
    }
  }, [stopRingtone, toast, startCallTimer]);

  // Reject incoming call
  const rejectCall = useCallback(async () => {
    try {
      stopRingtone();
      await _sip()?.rejectCall();

      // 🔧 FIX: Forçar reset do estado completo + rejeitar invitations enfileiradas
      let queuedToReject = [];
      setState((prev) => {
        queuedToReject = prev.callQueue || [];
        return {
          ...prev,
          callState: CallState.IDLE,
          incomingCall: null,
          callInfo: null,
          currentCallId: null,
          callQueue: [],
        };
      });
      rejectAllQueuedInvitations(queuedToReject);

      return true;
    } catch (error) {
      console.error("[VoIP] ❌ Failed to reject call:", error);
      // Mesmo se falhar, tentar resetar o estado
      let queuedToReject = [];
      setState((prev) => {
        queuedToReject = prev.callQueue || [];
        return {
          ...prev,
          callState: CallState.IDLE,
          incomingCall: null,
          callQueue: [],
        };
      });
      rejectAllQueuedInvitations(queuedToReject);
      return false;
    }
  }, [stopRingtone]);

  // 🔧 FIX: Rejeitar uma chamada específica da callQueue (não a principal).
  // Sem isso, clicar "Rejeitar" em uma chamada da fila rejeitava a chamada
  // principal por engano.
  const rejectQueuedCall = useCallback((queuedCall) => {
    if (!queuedCall) return false;
    try {
      const invitation = queuedCall.invitation;
      if (invitation && typeof invitation.reject === "function") {
        const result = invitation.reject({ statusCode: 486 });
        if (result && typeof result.catch === "function") result.catch(() => {});
      }
    } catch (e) {
      console.warn("[VoIP] Failed to reject queued call:", e?.message);
    }
    setState((prev) => ({
      ...prev,
      callQueue: (prev.callQueue || []).filter((c) => c !== queuedCall),
    }));
    return true;
  }, []);

  // Hang up current call
  const hangup = useCallback(async () => {
    try {
      stopRingtone();
      await _sip()?.hangup();
      stopCallTimer();
      
      if (hangupTimeoutRef.current) {
        clearTimeout(hangupTimeoutRef.current);
      }
      hangupTimeoutRef.current = setTimeout(() => {
        setState((prev) => {
          // Só reseta se ainda estiver em um estado de chamada
          if (prev.callState !== CallState.IDLE) {
            return {
              ...prev,
              callState: CallState.IDLE,
              callInfo: null,
              callStartTime: null,
              callDuration: 0,
              isMuted: false,
              isOnHold: false,
              incomingCall: null,
              currentCallId: null,
              callQueue: [],
              isInTransfer: false,
              transferState: TransferState.IDLE,
            };
          }
          return prev;
        });
      }, 2000); // Aguarda 2 segundos para o callback normal
      
      return true;
    } catch (error) {
      console.error("[VoIP] Failed to hang up:", error);
      // Mesmo em erro, tentar resetar o estado
      setState((prev) => ({
        ...prev,
        callState: CallState.IDLE,
        callInfo: null,
        incomingCall: null,
        callQueue: [],
        isInTransfer: false,
        transferState: TransferState.IDLE,
      }));
      return false;
    }
  }, [stopCallTimer, stopRingtone]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    const muted = _sip()?.toggleMute() ?? false;
    setState((prev) => ({
      ...prev,
      isMuted: muted,
    }));
    return muted;
  }, []);

  // Toggle hold
  const toggleHold = useCallback(async () => {
    try {
      const onHold = (await _sip()?.toggleHold()) ?? false;
      setState((prev) => ({
        ...prev,
        isOnHold: onHold,
        callState: onHold ? CallState.ON_HOLD : CallState.CONNECTED,
      }));
      return onHold;
    } catch (error) {
      console.error("[VoIP] Failed to toggle hold:", error);
      toast.error(i18n.t("voip.holdFailed"));
      return false;
    }
  }, [toast]);

  // Transfer call
  const transfer = useCallback(
    async (target, attended = false) => {
      try {
        const result = await _sip()?.transfer(target, attended);
        
        if (!attended) {
          toast.success(i18n.t("voip.transferSuccess"));
        }
        return result;
      } catch (error) {
        console.error("[VoIP] ❌ Failed to transfer:", error);
        toast.error(i18n.t("voip.transferFailed"));
        return false;
      }
    },
    [toast]
  );

  // Complete attended transfer
  const completeTransfer = useCallback(async () => {
    try {
      await _sip()?.attendedTransferComplete();
      // Estado é atualizado automaticamente via onTransferStateChange
      stopCallTimer();
      toast.success("Transferência concluída com sucesso!");
      return true;
    } catch (error) {
      console.error("[VoIP] Failed to complete transfer:", error);
      toast.error("Erro ao completar transferência");
      return false;
    }
  }, [stopCallTimer, toast]);

  // Cancel attended transfer and return to original call
  const cancelTransfer = useCallback(async () => {
    try {
      await _sip()?.attendedTransferCancel();
      // Estado é atualizado automaticamente via onTransferStateChange
      toast.info("Transferência cancelada. Voltando para chamada original.");
      return true;
    } catch (error) {
      console.error("[VoIP] Failed to cancel transfer:", error);
      toast.error("Erro ao cancelar transferência");
      return false;
    }
  }, [toast]);

  // Swap between original and consult call during attended transfer
  const swapTransferCalls = useCallback(async () => {
    try {
      await _sip()?.attendedTransferSwap();
      toast.info("Alternando entre chamadas...");
      return true;
    } catch (error) {
      console.error("[VoIP] Failed to swap calls:", error);
      toast.error("Erro ao alternar chamadas");
      return false;
    }
  }, [toast]);

  // Send DTMF
  const sendDTMF = useCallback((digit) => {
    return _sip()?.sendDTMF(digit);
  }, []);

  // Create ticket for current call
  const createTicketForCall = useCallback(async (queueId) => {
    if (!state.callInfo?.remoteNumber) {
      toast.error(i18n.t("voip.noCallActive"));
      return null;
    }

    if (!queueId) {
      toast.error("Selecione uma fila para criar o ticket");
      return null;
    }

    try {
      const { data } = await api.post("/api/voip/calls/new/ticket", {
        phoneNumber: state.callInfo.remoteNumber,
        contactName: state.callInfo.remoteName,
        queueId,
        callDuration: state.callInfo.duration,
        direction: state.callInfo.direction,
      });

      // Update state with ticket info
      setState((prev) => ({
        ...prev,
        callInfo: {
          ...prev.callInfo,
          ticketId: data.ticket.id,
          contactId: data.contact.id,
        },
      }));

      toast.success(i18n.t("voip.ticketCreated"));
      return data;
    } catch (error) {
      console.error("[VoIP] Failed to create ticket:", error);
      toast.error(i18n.t("voip.ticketCreateFailed"));
      return null;
    }
  }, [state.callInfo, toast]);

  // Link current call to an existing ticket
  const linkToTicket = useCallback(async (ticketId) => {
    if (!state.currentCallId) {
      return false;
    }

    try {
      await api.post(`/api/voip/calls/${state.currentCallId}/link-ticket`, {
        ticketId,
      });

      setState((prev) => ({
        ...prev,
        callInfo: {
          ...prev.callInfo,
          ticketId,
        },
      }));

      toast.success(i18n.t("voip.callLinked"));
      return true;
    } catch (error) {
      console.error("[VoIP] Failed to link call to ticket:", error);
      toast.error(i18n.t("voip.linkFailed"));
      return false;
    }
  }, [state.currentCallId, toast]);

  // Get recording URL for a call
  const getRecording = useCallback(async (callId) => {
    try {
      const { data } = await api.get(`/api/voip/calls/${callId}/recording`);
      return data.recordingUrl;
    } catch (error) {
      console.error("[VoIP] Failed to get recording:", error);
      return null;
    }
  }, []);

  // Add recording URL to a call
  const addRecordingToCall = useCallback(async (callId, recordingUrl, recordingFileName) => {
    try {
      const { data } = await api.post(`/api/voip/calls/${callId}/recording`, {
        recordingUrl,
        recordingFileName,
      });
      toast.success(i18n.t("voip.recordingAdded"));
      return data;
    } catch (error) {
      console.error("[VoIP] Failed to add recording:", error);
      toast.error(i18n.t("voip.recordingAddFailed"));
      return null;
    }
  }, [toast]);

  // Get call history
  const getCallHistory = useCallback(async (params = {}) => {
    try {
      const queryParams = new URLSearchParams({
        pageNumber: params.page || 1,
        limit: params.limit || 20,
        ...params,
      }).toString();
      
      const { data } = await api.get(`/api/voip/calls?${queryParams}`);
      return data;
    } catch (error) {
      console.error("[VoIP] Failed to get call history:", error);
      return { calls: [], count: 0, hasMore: false };
    }
  }, []);

  // Get call details
  const getCallDetails = useCallback(async (callId) => {
    try {
      const { data } = await api.get(`/api/voip/calls/${callId}`);
      return data;
    } catch (error) {
      console.error("[VoIP] Failed to get call details:", error);
      return null;
    }
  }, []);

  // Format call duration
  const formatDuration = useCallback((seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // Check if can make calls
  const canMakeCalls = state.isEnabled && state.isRegistered && state.callState === CallState.IDLE;

  // 🚀 PERF (Fase 0.1): Memoizar actions separadamente do state.
  // Actions são todas useCallback estáveis → useMemo identifica como "não mudou"
  // → consumidores que usam useVoipActions() não re-renderizam quando state muda.
  const actionsValue = useMemo(() => ({
    connect,
    disconnect,
    makeCall,
    answerCall,
    rejectCall,
    rejectQueuedCall,
    hangup,
    toggleMute,
    toggleHold,
    transfer,
    completeTransfer,
    cancelTransfer,
    swapTransferCalls,
    sendDTMF,
    setCallVolume,
    setRingVolume,
    setAutoConnect,
    setNotifications,
    clearMissedCalls,
    resetCircuitBreaker,
    openSoftphone,
    closeSoftphone,
    toggleSoftphone,
    setDialNumber,
    clearDialNumber,
    lookupContact,
    createTicketForCall,
    linkToTicket,
    getRecording,
    addRecordingToCall,
    getCallHistory,
    getCallDetails,
    formatDuration,
  }), [
    connect, disconnect, makeCall, answerCall, rejectCall, rejectQueuedCall,
    hangup, toggleMute, toggleHold, transfer, completeTransfer, cancelTransfer,
    swapTransferCalls, sendDTMF, setCallVolume, setRingVolume, setAutoConnect,
    setNotifications, clearMissedCalls, resetCircuitBreaker, openSoftphone, closeSoftphone, toggleSoftphone,
    setDialNumber, clearDialNumber, lookupContact, createTicketForCall,
    linkToTicket, getRecording, addRecordingToCall, getCallHistory,
    getCallDetails, formatDuration,
  ]);

  const stateValue = useMemo(() => ({
    ...state,
    canMakeCalls,
    formattedDuration: formatDuration(state.callDuration),
  }), [state, canMakeCalls, formatDuration]);

  // Legacy combined value (retro-compat com useVoip())
  // ⚠️ Re-renderiza a cada tick do callTimer. Migre para useVoipState/useVoipActions.
  const value = useMemo(() => ({
    ...stateValue,
    ...actionsValue,
  }), [stateValue, actionsValue]);

  return (
    <VoipActionsContext.Provider value={actionsValue}>
      <VoipStateContext.Provider value={stateValue}>
        <VoipContext.Provider value={value}>{children}</VoipContext.Provider>
      </VoipStateContext.Provider>
    </VoipActionsContext.Provider>
  );
};

export default VoipContext;

